import type { Level } from '@/api/types';
import { APP_STAGES } from '@/api/stageGrants';
import type { AppStage } from '@/api/stageGrants';
import { NavIcon } from '@/components/icons/NavIcon';
import LevelSegment from './LevelSegment';
import styles from './StageGrantsCard.module.css';

export type StageDraft = Partial<Record<AppStage, Level>>;
export type StageChangeKind = 'grant' | 'update' | 'revoke' | 'current';

export interface StageSyncNotice {
  failed: boolean;
  text: string;
}

export interface StageGrantsPanelState {
  loaded: boolean;
  count: number;
  editable: boolean;
  applying?: boolean;
  warning?: string;
  syncNotice?: StageSyncNotice;
  loadError: boolean;
  writeError?: string;
  footerSummary: string;
  buttonLabel: string;
  buttonDisabled: boolean;
  // 재시도·상태 확인 버튼 노출 여부 — 일반 변경 적용은 하단 액션바가 담당.
  showRetry?: boolean;
}

// Stage 권한 정책 한 줄 안내 — 추가 카드 제목 옆 '?'에서 보여준다.
export const STAGE_GRANT_GUIDE =
  'Stage 권한은 현재 해당 Stage에 속한 모든 게임에 적용됩니다. '
  + '게임의 Stage가 승격되거나 변경되면 그 게임은 이 권한에서 자동으로 제외됩니다. '
  + '특정 게임에 계속 접근해야 한다면 게임별 권한을 부여하세요. 게임별 권한은 Stage 변경과 무관하게 유지됩니다.';

export const stageLabel = (stage: AppStage) => stage === 'UA_OPS'
  ? 'UA Ops'
  : `Stage ${stage.replace('STAGE_', '')}`;

export default function StageGrantsPanel({
  regionLabel,
  detailsId,
  embedded = false,
  headless = false,
  title,
  expanded = true,
  onToggleExpanded,
  selected,
  visibleStages = APP_STAGES,
  changeKinds = {},
  state,
  emptyMessage,
  lockedMessage,
  onToggleStage,
  onSetStage,
  onApply,
  onRetry,
}: {
  regionLabel: string;
  detailsId: string;
  embedded?: boolean;
  // headless: 자체 접기/펼치기 헤더 없이 열 제목(title)만 두고 내용을 항상 펼친다. 아코디언은 상위 박스가 담당.
  headless?: boolean;
  title?: string;
  expanded?: boolean;
  onToggleExpanded?: () => void;
  selected: StageDraft;
  visibleStages?: readonly AppStage[];
  changeKinds?: Partial<Record<AppStage, StageChangeKind>>;
  state: StageGrantsPanelState;
  emptyMessage?: string;
  lockedMessage?: string;
  onToggleStage: (stage: AppStage) => void;
  onSetStage: (stage: AppStage, level: Level | null) => void;
  onApply: () => void;
  onRetry: () => void;
}) {
  const open = headless || expanded;
  return <section className={`${styles.card} ${embedded ? styles.embedded : ''} ${headless ? styles.headless : ''} ${state.loaded && state.count ? styles.active : ''} ${state.applying ? styles.applying : ''}`}
    aria-label={regionLabel} aria-busy={state.applying || undefined}>
    {headless && <div className={styles.columnHead}>
      <strong>{title}</strong>
      {state.loaded && <span className={styles.count}>{state.count}</span>}
    </div>}
    {!headless && <button type="button" className={styles.summary} aria-expanded={expanded}
      aria-controls={detailsId} onClick={onToggleExpanded} disabled={state.applying}>
      <div className={styles.row}>
        <div className={styles.info}>
          <div className={styles.heading}>
            <h2>Stage별 권한</h2>
            {state.loaded && <span className={styles.count}>{state.count}</span>}
          </div>
        </div>
        <span className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`} aria-hidden="true">
          <NavIcon name="chev-r" size={16} />
        </span>
      </div>
    </button>}
    {lockedMessage && <div className={styles.lockedNotice} role="status">
      <NavIcon name="lock" size={14} />
      <span>{lockedMessage}</span>
    </div>}
    {state.loadError && <div className={styles.loadError} role="alert">
      <span className={styles.loadErrorIcon} aria-hidden="true"><NavIcon name="info" size={16} /></span>
      <div className={styles.loadErrorCopy}>
        <strong>Stage 권한을 불러오지 못했습니다</strong>
        <span>잠시 후 다시 조회해 주세요.</span>
      </div>
      <button type="button" className={styles.loadErrorRetry} onClick={onRetry}>다시 조회</button>
    </div>}
    {open && <div id={detailsId} className={styles.details}>
      <div className={styles.stageTable}>
        <div className={styles.options} role="group" aria-label="Stage별 권한 옵션">
          {visibleStages.map(stage => {
            const level = selected[stage];
            const pending = changeKinds[stage] && changeKinds[stage] !== 'current';
            const kind = changeKinds[stage];
            // 추가 카드에서 kind==='revoke'는 보유 권한에서 빼낸 저장된 Stage — 게임의 권한 회수 행처럼 붉게 표시.
            const revoking = !embedded && kind === 'revoke';
            return <div key={stage} className={`${styles.option} ${embedded && kind === 'grant' ? styles.optionPending : ''} ${revoking ? styles.optionRevoke : ''} ${embedded && pending ? styles.optionEnter : ''}`}>
              {/* 넣기는 Viewer/Editor 버튼으로만, 빼기는 보유 패널의 행 끝 X로만. 이름은 표시용. */}
              <span className={styles.stageIcon} aria-hidden="true"><NavIcon name="grid" size={14} /></span>
              <div className={styles.stageName} data-stage={stage}>
                {stageLabel(stage)}
                {!embedded && lockedMessage && <span aria-hidden="true">관리자 전용</span>}
              </div>
              {/* 우측: 게임 보유 행(gameRight)과 같은 배치 — 상태 배지, 역할 세그먼트, X */}
              <div className={styles.optionRight}>
                {revoking && <span className={`${styles.badge} ${styles.badgeRevoke}`}>권한 회수</span>}
                {embedded && kind === 'grant' && <span className={`${styles.badge} ${styles.badgeGrant}`}>추가 예정</span>}
                {embedded && kind === 'update' && <span className={`${styles.badge} ${styles.badgeUpdate}`}>역할 변경 예정</span>}
                <LevelSegment value={level ?? null} disabled={!state.editable}
                  groupLabel={`${stageLabel(stage)} 권한`}
                  onSelect={next => onSetStage(stage, next)} />
                {embedded && <button type="button" className={styles.removeBtn} disabled={!state.editable}
                  aria-label={`${stageLabel(stage)} ${kind === 'grant' ? '목록에서 선택 해제' : '권한 회수'}`}
                  title={kind === 'grant' ? '선택 해제' : '권한 회수 (추가 카드로 이동)'}
                  onClick={() => onToggleStage(stage)}>
                  <NavIcon name="x" size={14} color="#9B9B9B" />
                </button>}
              </div>
            </div>;
          })}
          {!visibleStages.length && state.loaded && <div className={styles.emptyStageList}>{emptyMessage}</div>}
        </div>
      </div>
      {state.warning && <p className={styles.warning}>{state.warning}</p>}
      {state.syncNotice && <div className={state.syncNotice.failed ? styles.error : styles.syncStatus}
        role={state.syncNotice.failed ? 'alert' : 'status'}>
        <span className={styles.syncStatusIcon} aria-hidden="true"><NavIcon name="check" size={14} /></span>
        <span>{state.syncNotice.text}</span>
      </div>}
      {state.writeError && <p className={styles.error} role="alert">{state.writeError}</p>}
      {state.showRetry && <div className={styles.applyRow}>
        <span>{state.footerSummary}</span>
        <button type="button" className={styles.apply} disabled={state.buttonDisabled} onClick={onApply}>
          {state.buttonLabel}
        </button>
      </div>}
    </div>}
  </section>;
}
